import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2, FiEdit, FiUpload, FiX, FiCheck, FiArrowLeft, FiTrendingUp } from 'react-icons/fi';
import LineChart from '../../../components/charts/LineChart';

const SitePositions = () => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Состояние для сайта
  const [site, setSite] = useState(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteError, setSiteError] = useState('');
  
  // Состояние для позиций
  const [positions, setPositions] = useState([]);
  const [stats, setStats] = useState({});
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState('');
  
  // Состояние для модалок
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [selectedQuery, setSelectedQuery] = useState(null);
  
  // Состояние для режима редактирования
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Состояние для фильтров
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return thirtyDaysAgo.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [searchId, setSearchId] = useState('1');
  
  // Состояние для форм
  const [formData, setFormData] = useState({
    site_id: '',
    search_id: '1',
    date: new Date().toISOString().split('T')[0],
    query: '',
    position: ''
  });
  
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSearchId, setImportSearchId] = useState('1');

  // --- ДОБАВЛЯЮ СОСТОЯНИЕ ДЛЯ СТАТИСТИКИ ---
  const [lastDayStats, setLastDayStats] = useState({});

  // --- ДОБАВЛЯЮ СОСТОЯНИЕ ДЛЯ СТРАНИЦ ---
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState('');

  // --- ДОБАВЛЯЮ СОСТОЯНИЕ ДЛЯ ПОСЛЕДНЕЙ ДАТЫ ---
  const [lastDataDate, setLastDataDate] = useState('');

  // Загрузка информации о сайте
  useEffect(() => {
    if (siteId) {
      fetchSite();
    }
  }, [siteId]);

  // Загрузка позиций при изменении фильтров
  useEffect(() => {
    if (siteId) {
      fetchPositions();
    }
  }, [siteId, dateFrom, dateTo, searchId]);

  // --- ИЗМЕНЯЮ ЗАГРУЗКУ ПОЗИЦИЙ (СОХРАНЯЮ ПОСЛЕДНЮЮ ДАТУ) ---
  const fetchPositions = async () => {
    try {
      setPositionsLoading(true);
      setPositionsError('');
      
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (searchId) params.append('search_id', searchId);

      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}/positions?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const positionsData = data.data.positions || [];
        setPositions(positionsData);
        
        // Находим последнюю дату из данных
        if (positionsData.length > 0) {
          const dates = positionsData.map(p => p.date).sort();
          const lastDate = dates[dates.length - 1];
          setLastDataDate(lastDate);
          // Загружаем статистику за последнюю дату из данных
          fetchLastDayStats(lastDate);
        } else {
          setLastDataDate('');
          setLastDayStats({});
        }
      } else {
        throw new Error(data.message || 'Ошибка загрузки позиций');
      }
    } catch (error) {
      setPositionsError(error.message);
    } finally {
      setPositionsLoading(false);
    }
  };

  // --- ДОБАВЛЯЮ ФУНКЦИЮ ДЕКОДИРОВКИ BASE64 ---
  const decodeBase64 = (str) => {
    if (!str) return '';
    const isBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(str) && str.length % 4 === 0;
    if (isBase64) {
      try {
        const decoded = atob(str);
        try {
          return decodeURIComponent(escape(decoded));
        } catch {
          return decoded;
        }
      } catch {
        return str;
      }
    } else {
      return str;
    }
  };

  // --- ДОБАВЛЯЮ ФУНКЦИЮ ПАРСИНГА KEYWORDS ---
  const parseKeywords = (keyData) => {
    if (!keyData) return [];
    try {
      const decoded = decodeBase64(keyData);
      const keywords = [];
      const matches = decoded.match(/"([^\"]+)"/g);
      if (matches) {
        const extractedKeywords = matches.map(match => match.replace(/"/g, ''));
        keywords.push(...extractedKeywords.map(keyword => {
          try {
            return decodeURIComponent(escape(keyword));
          } catch {
            return keyword;
          }
        }));
      }
      if (keywords.length === 0) {
        const altMatches = decoded.match(/s:\d+:"([^\"]+)"/g);
        if (altMatches) {
          const altKeywords = altMatches.map(match => {
            const content = match.match(/s:\d+:"([^\"]+)"/);
            return content ? content[1] : '';
          }).filter(Boolean);
          keywords.push(...altKeywords.map(keyword => {
            try {
              return decodeURIComponent(escape(keyword));
            } catch {
              return keyword;
            }
          }));
        }
      }
      return keywords;
    } catch (error) {
      return [];
    }
  };

  // --- ДОБАВЛЯЮ ЗАГРУЗКУ СТРАНИЦ ---
  useEffect(() => {
    if (siteId) {
      fetchPages();
    }
  }, [siteId]);

  // --- ДОБАВЛЯЮ useEffect ДЛЯ ОБНОВЛЕНИЯ СТАТИСТИКИ ПРИ ИЗМЕНЕНИИ ФИЛЬТРОВ ---
  useEffect(() => {
    if (siteId && lastDataDate) {
      fetchLastDayStats(lastDataDate);
    }
  }, [siteId, searchId, lastDataDate]);

  const fetchSite = async () => {
    try {
      setSiteLoading(true);
      setSiteError('');
      
      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSite(data.site);
      } else {
        throw new Error(data.message || 'Ошибка загрузки сайта');
      }
    } catch (error) {
      setSiteError(error.message);
    } finally {
      setSiteLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      setPagesLoading(true);
      setPagesError('');
      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}/pages`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setPages(data.pages || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки страниц');
      }
    } catch (error) {
      setPagesError(error.message);
    } finally {
      setPagesLoading(false);
    }
  };

  // --- ИЗМЕНЯЮ ФУНКЦИЮ ЗАГРУЗКИ СТАТИСТИКИ (ПРИНИМАЕТ ДАТУ) ---
  const fetchLastDayStats = async (targetDate) => {
    if (!targetDate) return;
    
    try {
      const params = new URLSearchParams();
      params.append('date_from', targetDate);
      params.append('date_to', targetDate);
      if (searchId) params.append('search_id', searchId);

      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}/positions?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setLastDayStats(data.data.stats || {});
      } else {
        setLastDayStats({});
      }
    } catch (error) {
      setLastDayStats({});
    }
  };

  const handleCreatePosition = async () => {
    // Валидация формы
    if (!formData.query.trim()) {
      setPositionsError('Запрос не может быть пустым');
      return;
    }
    
    if (formData.query.trim().length < 3) {
      setPositionsError('Запрос должен содержать минимум 3 символа');
      return;
    }
    
    if (!formData.position || parseInt(formData.position) <= 0) {
      setPositionsError('Позиция должна быть положительным числом');
      return;
    }

    try {
      setPositionsLoading(true);
      setPositionsError('');
      
      const csrfToken = await getCsrfToken();
      
      const response = await fetch(`${API_URL}${currentLang}/seo/positions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          site_id: parseInt(siteId)
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowCreateModal(false);
        setFormData({
          site_id: '',
          search_id: '1',
          date: new Date().toISOString().split('T')[0],
          query: '',
          position: ''
        });
        fetchPositions();
      } else {
        throw new Error(data.message || 'Ошибка создания позиции');
      }
    } catch (error) {
      setPositionsError(error.message);
    } finally {
      setPositionsLoading(false);
    }
  };

  const handleUpdatePosition = async () => {
    try {
      setPositionsLoading(true);
      setPositionsError('');
      
      const csrfToken = await getCsrfToken();
      
      const response = await fetch(`${API_URL}${currentLang}/seo/positions/${editingPosition.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          position: parseInt(formData.position)
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowEditModal(false);
        setEditingPosition(null);
        setFormData({
          site_id: '',
          search_id: '1',
          date: new Date().toISOString().split('T')[0],
          query: '',
          position: ''
        });
        fetchPositions();
      } else {
        throw new Error(data.message || 'Ошибка обновления позиции');
      }
    } catch (error) {
      setPositionsError(error.message);
    } finally {
      setPositionsLoading(false);
    }
  };

  const handleDeletePosition = async (positionId) => {
    try {
      setPositionsLoading(true);
      setPositionsError('');
      
      const csrfToken = await getCsrfToken();
      
      const response = await fetch(`${API_URL}${currentLang}/seo/positions/${positionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowDeleteModal(false);
        fetchPositions();
      } else {
        throw new Error(data.message || 'Ошибка удаления позиции');
      }
    } catch (error) {
      setPositionsError(error.message);
    } finally {
      setPositionsLoading(false);
    }
  };

  const handleImportPositions = async () => {
    if (!importFile) {
      setPositionsError('Выберите файл для импорта');
      return;
    }

    try {
      setImporting(true);
      setPositionsError('');
      
      const csrfToken = await getCsrfToken();
      
      const formData = new FormData();
      formData.append('csv_file', importFile);
      formData.append('search_id', importSearchId);

      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}/positions/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowImportModal(false);
        setImportFile(null);
        setImportSearchId('1');
        fetchPositions();
      } else {
        throw new Error(data.message || 'Ошибка импорта позиций');
      }
    } catch (error) {
      setPositionsError(error.message);
    } finally {
      setImporting(false);
    }
  };

  const openEditModal = (position) => {
    setEditingPosition(position);
    setFormData({
      ...formData,
      position: position.position.toString(),
      query: position.query,
      date: position.date
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (position) => {
    setEditingPosition(position);
    setShowDeleteModal(true);
  };

  const openChartModal = (query) => {
    setSelectedQuery(query);
    setShowChartModal(true);
  };

  const openCreateModalWithData = (query, date) => {
    // Проверяем, что запрос не пустой и имеет минимум 3 символа
    if (!query.trim() || query.trim().length < 3) {
      setPositionsError('Запрос должен содержать минимум 3 символа');
      return;
    }
    
    setFormData({
      ...formData,
      query: query,
      date: date,
      search_id: searchId,
      position: ''
    });
    setShowCreateModal(true);
  };

  // Функция для группировки данных по запросам и датам
  const processPositionsData = () => {
    if (!positions.length) return { queries: [], dates: [], data: {} };

    // Получаем уникальные запросы и даты
    const uniqueQueries = [...new Set(positions.map(p => p.query))].sort();
    const uniqueDates = [...new Set(positions.map(p => p.date))].sort((a, b) => new Date(a) - new Date(b));

    // Создаем объект для хранения данных
    const data = {};
    
    // Инициализируем данные
    uniqueQueries.forEach(query => {
      data[query] = {};
      uniqueDates.forEach(date => {
        data[query][date] = null;
      });
    });

    // Заполняем данные позициями
    positions.forEach(position => {
      if (data[position.query] && data[position.query][position.date] === null) {
        data[position.query][position.date] = {
          id: position.id,
          position: position.position,
          search_id: position.search_id,
          query: position.query,
          date: position.date
        };
      }
    });

    return { queries: uniqueQueries, dates: uniqueDates, data };
  };

  // Функция для получения цвета ячейки
  const getCellColor = (position) => {
    if (!position) return 'bg-gray-100 dark:bg-gray-700';
    if (position.position <= 3) return 'bg-green-200 dark:bg-green-200';
    if (position.position <= 5) return 'bg-green-100 dark:bg-green-100';
    if (position.position <= 10) return 'bg-blue-100 dark:bg-blue-900';
    if (position.position <= 20) return 'bg-yellow-100 dark:bg-yellow-900';
    return 'bg-gray-200 dark:bg-gray-600';
  };

  // Функция для получения цвета текста
  const getTextColor = (position) => {
    if (!position) return 'text-gray-500 dark:text-gray-400';
    if (position.position <= 6) return 'text-green-800 dark:text-green-200';
    if (position.position <= 10) return 'text-blue-800 dark:text-blue-200';
    if (position.position <= 20) return 'text-yellow-800 dark:text-yellow-200';
    return 'text-gray-700 dark:text-gray-300';
  };

  const getSearchEngineName = (searchId) => {
    const engines = {
      '1': 'Google',
      '2': 'Yandex',
      '3': 'Bing'
    };
    return engines[searchId] || `Поисковая система ${searchId}`;
  };

  const getSearchEngineUrl = (searchId, query) => {
    const engines = {
      '1': `https://www.google.com/search?q=${encodeURIComponent(query)}`,
      '2': `https://yandex.ru/search/?text=${encodeURIComponent(query)}`,
      '3': `https://www.bing.com/search?q=${encodeURIComponent(query)}`
    };
    return engines[searchId] || '#';
  };

  const getSearchEngineIcon = (searchId) => {
    const engines = {
      '1': (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      ),
      '2': (
        <span className="w-4 h-4 flex items-center justify-center font-bold text-sm">Я</span>
      ),
      '3': (
        <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
          <div className="bg-red-500 rounded-sm"></div>
          <div className="bg-green-500 rounded-sm"></div>
          <div className="bg-blue-500 rounded-sm"></div>
          <div className="bg-yellow-500 rounded-sm"></div>
        </div>
      )
    };
    return engines[searchId] || null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Неверная дата';
    return date.toLocaleDateString('ru-RU');
  };

  const handleBackToSites = () => {
    navigate('/seo/positions');
  };

  // Функции для быстрого выбора периодов
  const setLast30Days = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
    setDateTo(now.toISOString().split('T')[0]);
  };

  const setCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const lastDayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    setDateFrom(firstDay);
    setDateTo(lastDayStr);
  };

  const setPreviousMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() - 1;
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const lastDayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    setDateFrom(firstDay);
    setDateTo(lastDayStr);
  };

  const setCurrentQuarter = () => {
    const now = new Date();
    const year = now.getFullYear();
    const quarter = Math.floor(now.getMonth() / 3);
    const quarterStartMonth = quarter * 3;
    const quarterEndMonth = quarterStartMonth + 2;
    
    const firstDay = `${year}-${String(quarterStartMonth + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, quarterEndMonth + 1, 0).getDate();
    const lastDayStr = `${year}-${String(quarterEndMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    
    setDateFrom(firstDay);
    setDateTo(lastDayStr);
  };

  const setCurrentYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const firstDay = `${year}-01-01`;
    const lastDay = `${year}-12-31`;
    setDateFrom(firstDay);
    setDateTo(lastDay);
  };

  // Обрабатываем данные для отображения
  const { queries, dates, data } = processPositionsData();

  // Проверка валидности формы создания
  const isCreateDisabled =
    !formData.query.trim() ||
    formData.query.trim().length < 3 ||
    !formData.position ||
    parseInt(formData.position) <= 0;

  // Проверка валидности формы редактирования
  const isEditDisabled =
    !formData.position ||
    parseInt(formData.position) <= 0;

  // Показываем загрузку сайта
  if (siteLoading) {
    return (
      <div className="mt-8 sm:mt-12 mb-4 sm:mb-8 flex justify-center items-center">
        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500"></div>
        <p className="ml-2 sm:ml-3 text-gray-600 text-sm sm:text-base">Загрузка сайта...</p>
      </div>
    );
  }

  // Показываем ошибку сайта
  if (siteError) {
    return (
      <div className="mt-8 sm:mt-12 mb-4 sm:mb-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded text-sm">
          Ошибка загрузки сайта: {siteError}
        </div>
        <button
          onClick={handleBackToSites}
          className="mt-3 sm:mt-4 flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm sm:text-base"
        >
          <FiArrowLeft className="h-4 w-4" />
          Назад к сайтам
        </button>
      </div>
    );
  }

  // Показываем основной интерфейс
  return (
    <div className="mt-8 sm:mt-12 mb-4 sm:mb-8 flex flex-col gap-6 sm:gap-12">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-lg sm:shadow-xl dark:bg-navy-800">
        <div className="bg-gradient-to-r from-gray-100 to-blue-100 rounded-t-lg sm:rounded-t-xl p-4 sm:p-6 mb-4 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-blue-500">
                SEO Позиции
              </h1>
              <div className="flex items-center mt-1 sm:mt-2">
                <img src={`https://www.google.com/s2/favicons?domain=${site?.dns || ''}`} alt="favicon" className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="text-gray-900 text-sm sm:text-base truncate">{site?.dns || ''}</span>
              </div>
            </div>
            <button
              onClick={handleBackToSites}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/20 text-blue-500 rounded-lg hover:bg-white/30 transition-colors border border-blue-500 text-sm sm:text-base w-fit"
            >
              <FiArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Назад к сайтам</span>
              <span className="sm:hidden">Назад</span>
            </button>
          </div>
        </div>
        <div className="overflow-x-auto px-0 pt-0 pb-2">
          {/* Фильтры */}
          <div className="mb-4 sm:mb-6 px-3 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <input
                type="date"
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:border-gray-600 dark:text-white text-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="Дата с"
              />
              
              <input
                type="date"
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:border-gray-600 dark:text-white text-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="Дата по"
              />
            </div>
            
            {/* Быстрые кнопки периодов */}
            <div className="flex flex-wrap gap-1 sm:gap-2 mb-3 sm:mb-4">
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800 text-xs sm:text-sm"
                onClick={setLast30Days}
              >
                30 дней
              </button>
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800 text-xs sm:text-sm"
                onClick={setCurrentMonth}
              >
                <span className="hidden sm:inline">Текущий месяц</span>
                <span className="sm:hidden">Месяц</span>
              </button>
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800 text-xs sm:text-sm"
                onClick={setPreviousMonth}
              >
                <span className="hidden sm:inline">Прошлый месяц</span>
                <span className="sm:hidden">Прошлый</span>
              </button>
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800 text-xs sm:text-sm"
                onClick={setCurrentQuarter}
              >
                <span className="hidden sm:inline">Текущий квартал</span>
                <span className="sm:hidden">Квартал</span>
              </button>
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors dark:bg-orange-900 dark:text-orange-200 dark:hover:bg-orange-800 text-xs sm:text-sm"
                onClick={setCurrentYear}
              >
                <span className="hidden sm:inline">Текущий год</span>
                <span className="sm:hidden">Год</span>
              </button>
            </div>
            
            {/* Кнопки поисковых систем */}
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <button
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${
                  searchId === '1' 
                    ? 'border border-blue-500 text-blue-500' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 border border-gray-300'
                }`}
                onClick={() => setSearchId('1')}
              >
                {getSearchEngineIcon('1')}
                <span className="hidden sm:inline">Google</span>
                <span className="sm:hidden">G</span>
              </button>
              <button
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${
                  searchId === '2' 
                    ? 'border border-blue-500 text-red-500' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 border border-gray-300'
                }`}
                onClick={() => setSearchId('2')}
              >
                {getSearchEngineIcon('2')}
                <span className="hidden sm:inline">Yandex</span>
                <span className="sm:hidden">Я</span>
              </button>
              {/*
              <button
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${
                  searchId === '3' 
                    ? 'border border-blue-500 text-green-500' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 border border-gray-300'
                }`}
                onClick={() => setSearchId('3')}
              >
                {getSearchEngineIcon('3')}
                <span className="hidden sm:inline">Bing</span>
                <span className="sm:hidden">B</span>
              </button>
              */}
            </div>
          </div>


          {/* Статистика за последний день из данных */}
          {Object.keys(lastDayStats).length > 0 && lastDataDate && (
            <div className="mb-4 sm:mb-6 px-3 sm:px-6">
              <div className="mb-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Статистика за {formatDate(lastDataDate)}:
                </p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                <div className="bg-white p-3 sm:p-4 rounded-lg shadow dark:bg-navy-700">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">
                    <span className="hidden sm:inline">Позиций</span>
                    <span className="sm:hidden">Позиций</span>
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {lastDayStats.total_positions || 0}
                  </p>
                </div>
                <div className="bg-white p-3 sm:p-4 rounded-lg shadow dark:bg-navy-700">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">
                    <span className="hidden sm:inline">Запросов</span>
                    <span className="sm:hidden">Запросов</span>
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {lastDayStats.unique_queries || 0}
                  </p>
                </div>
                <div className="bg-white p-3 sm:p-4 rounded-lg shadow dark:bg-navy-700">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">
                    Топ-3
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-green-600">
                    {lastDayStats.top3_count || 0}
                  </p>
                </div>
                <div className="bg-white p-3 sm:p-4 rounded-lg shadow dark:bg-navy-700">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">
                    Топ-10
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">
                    {lastDayStats.top10_count || 0}
                  </p>
                </div>
              </div>
            </div>
          )}

          {user?.role === 'admin' && (
          <>
          {/* Переключатель режимов */}
          <div className="mb-4 sm:mb-6 px-3 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-4">
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  Режим:
                </span>
                <div className="flex bg-gray-100 rounded-lg p-1 dark:bg-gray-700">
                  <button
                    className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                      !isEditMode
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-600 dark:text-blue-400'
                        : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                    onClick={() => setIsEditMode(false)}
                  >
                    <span className="hidden sm:inline">Просмотр</span>
                    <span className="sm:hidden">Просмотр</span>
                  </button>
                  <button
                    className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                      isEditMode
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-600 dark:text-blue-400'
                        : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                    onClick={() => setIsEditMode(true)}
                  >
                    <span className="hidden sm:inline">Редактирование</span>
                    <span className="sm:hidden">Редакт.</span>
                  </button>
                </div>
              </div>
              {isEditMode && (
                <div className="text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded">
                  <span className="hidden sm:inline">Режим редактирования активен</span>
                  <span className="sm:hidden">Редактирование</span>
                </div>
              )}
            </div>
          </div>
          </>
          )}

          {/* Кнопки действий */}
          <div className="mb-4 sm:mb-6 px-3 sm:px-6">
            {isEditMode && (
              <div className="flex flex-wrap gap-2">
                <button
                  className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      search_id: searchId
                    });
                    setShowCreateModal(true);
                  }}
                >
                  <FiPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">Добавить позицию</span>
                  <span className="sm:hidden">Добавить</span>
                </button>
                
                <button
                  className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors dark:border-gray-600 dark:text-white dark:hover:bg-gray-700 text-sm"
                  onClick={() => {
                    setImportSearchId(searchId);
                    setShowImportModal(true);
                  }}
                >
                  <FiUpload className="h-4 w-4" />
                  <span className="hidden sm:inline">Импорт из CSV</span>
                  <span className="sm:hidden">Импорт</span>
                </button>
              </div>
            )}
          </div>

          {/* Ошибки */}
          {positionsError && (
            <div className="mb-4 sm:mb-6 px-3 sm:px-6">
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded text-sm dark:bg-red-900 dark:border-red-700 dark:text-red-200">
                {positionsError}
              </div>
            </div>
          )}

          {/* Таблица позиций */}
          <div className="px-3 sm:px-6">
            {positionsLoading || pagesLoading ? (
              <div className="flex justify-center items-center h-24 sm:h-32">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : pagesError ? (
              <div className="text-center py-6 sm:py-8 text-red-500">{pagesError}</div>
            ) : pages.length > 0 ? (
              pages.map((page) => {
                const keywords = parseKeywords(page.key);
                if (!keywords.length) return null;
                return (
                  <div key={page.id} className="mb-8 border rounded-lg shadow bg-white dark:bg-navy-800">
                    <div className="px-4 py-2 border-b bg-gray-50 dark:bg-navy-700 rounded-t-lg">
                      <span className="font-semibold text-blue-600">{page.url}</span>
                      <span className="ml-2 text-xs text-gray-500">({keywords.length} ключевых запросов)</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[640px] table-auto">
                        <thead>
                          <tr>
                            <th className="border-b py-2 px-3 text-left sticky left-0 bg-white dark:bg-navy-800 z-10 w-[200px] md:w-[300px]">
                              <p className="text-xs font-medium uppercase text-gray-500">Запрос</p>
                            </th>
                            {processPositionsData().dates.map((date) => (
                              <th key={date} className="border-b py-2 px-2 text-center min-w-[100px]">
                                <button
                                  onClick={() => {
                                    setDateFrom(date);
                                    setDateTo(date);
                                  }}
                                  className="text-xs font-medium uppercase text-gray-500 hover:text-blue-600 hover:underline cursor-pointer transition-colors"
                                  title={`Фильтр по дате ${formatDate(date)}`}
                                >
                                  {formatDate(date)}
                                </button>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {keywords.map((query) => (
                            <tr key={query} className="hover:opacity-80 hover:text-blue-500 hover:bg-blue-50/50">
                              <td className="bg-white py-2 px-3 text-left sticky left-0 border-r border-gray-200 dark:border-gray-600 min-w-[150px] z-10">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs font-medium truncate max-w-[220px]">
                                    <a
                                      href={getSearchEngineUrl(searchId, query)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={`Проверить позицию в ${getSearchEngineName(searchId)}`}
                                      className="hover:underline"
                                    >
                                      {query}
                                    </a>
                                  </p>
                                  <button
                                    onClick={() => openChartModal(query)}
                                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0"
                                    title="Показать график динамики"
                                  >
                                    <FiTrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </button>
                                </div>
                              </td>
                              {processPositionsData().dates.map((date) => {
                                const posData = positions.find(
                                  p => p.query === query && p.date === date && String(p.search_id) === String(searchId)
                                );
                                return (
                                  <td
                                    key={`${query}-${date}`}
                                    className={`py-2 px-2 text-center relative group ${getCellColor(posData)} border-b border-gray-200 dark:border-gray-600`}
                                  >
                                    {posData ? (
                                      <div className="flex flex-col items-center">
                                        <a
                                          href={getSearchEngineUrl(posData.search_id, posData.query)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`text-xs font-medium ${getTextColor(posData)} hover:underline cursor-pointer`}
                                          title={`Проверить позицию в ${getSearchEngineName(posData.search_id)}`}
                                        >
                                          {posData.position}
                                        </a>
                                        <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                                          {getSearchEngineName(posData.search_id)}
                                        </span>
                                        {isEditMode && (
                                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="flex gap-1">
                                              <button
                                                className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                                onClick={() => openEditModal(posData)}
                                                title="Редактировать"
                                              >
                                                <FiEdit className="h-3 w-3" />
                                              </button>
                                              <button
                                                className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                                onClick={() => openDeleteModal(posData)}
                                                title="Удалить"
                                              >
                                                <FiTrash2 className="h-3 w-3" />
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="flex flex-col items-center">
                                        <span className="text-gray-400 dark:text-gray-500">
                                          <FiX className="h-3 w-3 sm:h-4 sm:w-4" />
                                        </span>
                                        {isEditMode && (
                                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                              className="p-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                              onClick={() => openCreateModalWithData(query, date)}
                                              title="Добавить позицию"
                                            >
                                              <FiPlus className="h-3 w-3" />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 sm:py-8">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Нет страниц с ключевыми запросами
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 px-2">
                  Добавьте страницы с ключевыми словами для отображения позиций
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Модалка создания позиции */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-0">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md dark:bg-navy-800 border">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-lg font-semibold dark:text-white">Добавить позицию</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Поисковый запрос"
                  className={`w-full px-2 sm:px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:text-white text-sm ${
                    formData.query.trim() && formData.query.trim().length < 3 
                      ? 'border-red-500 focus:ring-red-500 dark:border-red-500' 
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  value={formData.query}
                  onChange={(e) => setFormData({ ...formData, query: e.target.value })}
                />
                {formData.query.trim() && formData.query.trim().length < 3 && (
                  <p className="text-red-500 text-xs mt-1">Запрос должен содержать минимум 3 символа</p>
                )}
              </div>
              <input
                type="date"
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:border-gray-600 dark:text-white text-sm"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
              <div>
                <input
                  type="number"
                  placeholder="Позиция"
                  min="1"
                  className={`w-full px-2 sm:px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:text-white text-sm ${
                    formData.position && parseInt(formData.position) <= 0 
                      ? 'border-red-500 focus:ring-red-500 dark:border-red-500' 
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                />
                {formData.position && parseInt(formData.position) <= 0 && (
                  <p className="text-red-500 text-xs mt-1">Позиция должна быть положительным числом</p>
                )}
              </div>
              <select
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:border-gray-600 dark:text-white text-sm"
                value={formData.search_id}
                onChange={(e) => setFormData({ ...formData, search_id: e.target.value })}
              >
                <option value="1">Google</option>
                <option value="2">Yandex</option>
                <option value="3">Bing</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-4 sm:mt-6">
              <button
                className="px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors dark:text-gray-400 dark:hover:text-gray-200 text-sm"
                onClick={() => setShowCreateModal(false)}
              >
                Отмена
              </button>
              <button
                className="px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 text-sm"
                onClick={handleCreatePosition}
                disabled={positionsLoading || isCreateDisabled}
              >
                {positionsLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Создать'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка редактирования позиции */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-0">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md dark:bg-navy-800 border">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-lg font-semibold dark:text-white">Редактировать позицию</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Запрос: {editingPosition?.query}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Дата: {formatDate(editingPosition?.date)}
              </p>
              <div>
                <input
                  type="number"
                  placeholder="Позиция"
                  min="1"
                  className={`w-full px-2 sm:px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:text-white text-sm ${
                    formData.position && parseInt(formData.position) <= 0 
                      ? 'border-red-500 focus:ring-red-500 dark:border-red-500' 
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                />
                {formData.position && parseInt(formData.position) <= 0 && (
                  <p className="text-red-500 text-xs mt-1">Позиция должна быть положительным числом</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4 sm:mt-6">
              <button
                className="px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors dark:text-gray-400 dark:hover:text-gray-200 text-sm"
                onClick={() => setShowEditModal(false)}
              >
                Отмена
              </button>
              <button
                className="px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 text-sm"
                onClick={handleUpdatePosition}
                disabled={positionsLoading || isEditDisabled}
              >
                {positionsLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Обновить'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка импорта */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-0">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md dark:bg-navy-800 border">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-lg font-semibold dark:text-white">Импорт позиций из CSV</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Загрузите CSV файл с позициями. Формат файла:
              </p>
              <div className="bg-gray-50 p-3 sm:p-4 rounded-lg dark:bg-gray-700">
                <p className="text-xs font-mono text-gray-600 dark:text-gray-400">
                  Запросы;07.07.2025;01.07.2025;30.06.2025<br />
                  "сайдинг купить";35;31;23
                </p>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:border-gray-600 dark:text-white text-sm"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Поисковая система
                </label>
                <select
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:border-gray-600 dark:text-white text-sm"
                  value={importSearchId}
                  onChange={(e) => setImportSearchId(e.target.value)}
                >
                  <option value="1">Google</option>
                  <option value="2">Yandex</option>
                  <option value="3">Bing</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4 sm:mt-6">
              <button
                className="px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors dark:text-gray-400 dark:hover:text-gray-200 text-sm"
                onClick={() => setShowImportModal(false)}
              >
                Отмена
              </button>
              <button
                className="px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 text-sm"
                onClick={handleImportPositions}
                disabled={importing || !importFile}
              >
                {importing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Импортировать'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка удаления */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-0">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-sm dark:bg-navy-800 border">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-lg font-semibold dark:text-white">Удалить позицию</h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">
              Вы уверены, что хотите удалить позицию для запроса "{editingPosition?.query}"?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors dark:text-gray-400 dark:hover:text-gray-200 text-sm"
                onClick={() => setShowDeleteModal(false)}
              >
                Отмена
              </button>
              <button
                className="px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
                onClick={() => handleDeletePosition(editingPosition?.id)}
                disabled={positionsLoading}
              >
                {positionsLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Удалить'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка с графиком динамики */}
      {showChartModal && selectedQuery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-0">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto dark:bg-navy-800">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-lg font-semibold dark:text-white">
                <span className="hidden sm:inline">Динамика позиций: "{selectedQuery}"</span>
                <span className="sm:hidden">Динамика: "{selectedQuery.length > 20 ? selectedQuery.substring(0, 20) + '...' : selectedQuery}"</span>
              </h2>
              <button
                onClick={() => setShowChartModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4 sm:space-y-6">
              {/* График для каждой поисковой системы */}
              {['1', '2', '3'].map((engineId) => {
                const engineData = dates.map(date => {
                  const positionData = data[selectedQuery]?.[date];
                  return {
                    date: formatDate(date),
                    position: positionData && String(positionData.search_id) === engineId ? positionData.position : null
                  };
                }).filter(item => item.position !== null);

                if (engineData.length === 0) return null;

                // Подготавливаем данные для ApexCharts
                const chartData = [{
                  name: getSearchEngineName(engineId),
                  data: engineData.map(item => item.position)
                }];

                // Определяем максимальное значение для оси Y
                const maxPosition = Math.max(...engineData.map(item => item.position));
                const yAxisMax = maxPosition > 10 ? maxPosition : 10;

                const chartOptions = {
                  chart: {
                    type: 'line',
                    toolbar: {
                      show: true,
                      tools: {
                        download: true,
                        selection: false,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true
                      }
                    }
                  },
                  stroke: {
                    curve: 'smooth',
                    width: 3
                  },
                  colors: ['#3B82F6'],
                  xaxis: {
                    categories: engineData.map(item => item.date),
                    labels: {
                      rotate: -45,
                      style: {
                        fontSize: '12px'
                      }
                    }
                  },
                  yaxis: {
                    title: {
                      text: 'Позиция',
                      style: {
                        fontSize: '14px'
                      }
                    },
                    reversed: true, // Лучшие позиции сверху
                    min: 1,
                    max: yAxisMax
                  },
                  tooltip: {
                    y: {
                      formatter: function (val) {
                        return `Позиция: ${val}`;
                      }
                    }
                  },
                  markers: {
                    size: 6,
                    colors: engineData.map(item => {
                      if (item.position <= 3) return '#10B981'; // зеленый
                      if (item.position <= 10) return '#3B82F6'; // синий
                      if (item.position <= 20) return '#F59E0B'; // желтый
                      return '#EF4444'; // красный
                    }),
                    strokeColors: '#fff',
                    strokeWidth: 2
                  },
                  grid: {
                    borderColor: '#e5e7eb',
                    strokeDashArray: 5
                  }
                };

                return (
                  <div key={engineId} className="bg-white rounded-lg p-4 sm:p-6 shadow-sm dark:bg-navy-700">
                    <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 dark:text-white">
                      {getSearchEngineName(engineId)}
                    </h3>
                    <div className="h-64 sm:h-80">
                      <LineChart series={chartData} options={chartOptions} />
                    </div>
                  </div>
                );
              })}
              
              {!['1', '2', '3'].some(engineId => {
                const engineData = dates.map(date => {
                  const positionData = data[selectedQuery]?.[date];
                  return positionData && String(positionData.search_id) === engineId ? positionData.position : null;
                }).filter(pos => pos !== null);
                return engineData.length > 0;
              }) && (
                <div className="text-center py-6 sm:py-8 text-gray-500 dark:text-gray-400">
                  Нет данных для отображения графика
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SitePositions;